import { useCan, useCanAnywhere } from '@/lib/permissions';
import { useNarrow } from '@/lib/useMedia';
import { ActionChat } from './ActionChat';
import { useSourceLink } from './sourceLink';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { BookOpen, Building2, CheckSquare, FileText, Megaphone, MessageSquare, Search, Send, ShieldCheck, Sparkles, Trash2 } from 'lucide-react';
import { answerSegments, type AssistantAnswer, type AssistantSource } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { sentenceCase } from '@/lib/format';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi, type AssistantOrigin } from '@/lib/store';
import { CloseLink, Badge, Button, Card, PageHeader, Spinner, Tabs } from '@/components/ui';
import { RelativeTime } from '@/components/time/RelativeTime';

let turnId = 0;
const ICON = { message: MessageSquare, task: CheckSquare, attachment: FileText, building: Building2, article: BookOpen, announcement: Megaphone, policy: ShieldCheck } as const;
const EXAMPLES = ['When is the bay 12 racking inspection?', 'What did Tom report about aisle C?', 'Which building is the trade counter in?', 'Any photos of the damaged shelving?'];

/** Shared by the side panel and the /assistant page. */
function useAsk() {
  const ws = useWorkspaceId();
  const setTurn = useUi(s => s.setAssistantTurn);
  return async (question: string, origin: AssistantOrigin) => {
    const q = question.trim();
    if (!q || !ws) return;
    const id = ++turnId;
    setTurn({ id, question: q, status: 'loading' });
    try {
      const result = await api.askAssistant(ws, { question: q, origin });
      setTurn({ id, question: q, status: 'done', result });
    } catch (e) {
      setTurn({ id, question: q, status: 'error', error: errorMessage(e) });
    }
    void queryClient.invalidateQueries({ queryKey: ['assistant', ws] });
  };
}

function SourceCard({ s, cited, onOpen }: { s: AssistantSource; cited: boolean; onOpen: (s: AssistantSource) => void }) {
  const Icon = ICON[s.type];
  const parts = s.snippet.split(/«|»/);
  return (
    <button id={`src-${s.n}`} onClick={() => onOpen(s)} className={clsx('block w-full rounded-lg border p-2.5 text-left text-xs transition hover:border-accent-line', cited ? 'border-accent-line/25 bg-accent-soft/40' : 'border-slate-200 dark:border-slate-800')}>
      <div className="flex items-center gap-1.5">
        <span className="flex h-4 min-w-4 items-center justify-center rounded bg-slate-200 px-1 text-[10px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-200">{s.n}</span>
        <Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
        <span className="truncate font-semibold text-slate-700 dark:text-slate-200">{s.title}</span>
        {s.at && <span className="ml-auto shrink-0 text-slate-500"><RelativeTime iso={s.at} /></span>}
      </div>
      {s.context && <div className="mt-0.5 truncate text-slate-500">{s.context}</div>}
      {s.snippet && <div className="mt-1 line-clamp-3 text-slate-600 dark:text-slate-300">{parts.map((p, i) => (i % 2 ? <mark key={i} className="rounded bg-orange-100 px-0.5 dark:bg-orange-900/60 dark:text-orange-100">{p}</mark> : <span key={i}>{p}</span>))}</div>}
    </button>
  );
}

function Answer({ r, onOpen }: { r: AssistantAnswer; onOpen: (s: AssistantSource) => void }) {
  const byN = new Map(r.sources.map(s => [s.n, s]));
  return (
    <div className="space-y-2">
      {r.answer && (
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-100">
          {answerSegments(r.answer, r.sources.length).map((seg, i) => seg.kind === 'text' ? <span key={i}>{seg.text}</span> : (
            <button key={i} title={byN.get(seg.n)?.title} onClick={() => onOpen(byN.get(seg.n)!)}
              className="mx-0.5 inline-flex h-4 min-w-4 -translate-y-0.5 items-center justify-center rounded bg-accent-soft px-1 align-middle text-[10px] font-bold text-accent-fg hover:bg-accent hover:text-on-accent">{seg.n}</button>
          ))}
        </div>
      )}
      {r.notice && <div className="rounded-md bg-orange-50 px-2.5 py-1.5 text-xs text-orange-900 dark:bg-orange-950/40 dark:text-orange-200">{r.notice}</div>}
      {r.sources.length > 0 ? (
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{r.answer ? 'Sources' : `${r.sources.length} result${r.sources.length === 1 ? '' : 's'}`}</div>
          {r.sources.map(s => <SourceCard key={s.n} s={s} cited={r.cited.includes(s.n)} onOpen={onOpen} />)}
        </div>
      ) : !r.answer && <p className="text-sm text-slate-500">Nothing you have access to matched. Try other words.</p>}
    </div>
  );
}

export function AssistantConversation({ origin, autoFocus }: { origin: AssistantOrigin; autoFocus?: boolean }) {
  const ws = useWorkspaceId();
  const turns = useUi(s => s.assistantTurns);
  const clear = useUi(s => s.clearAssistant);
  const take = useUi(s => s.takeAssistantPending);
  const pending = useUi(s => s.assistantPending);
  const ask = useAsk();
  const [q, setQ] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const status = useQuery({ queryKey: ['assistant', ws, 'status'], queryFn: () => api.assistantStatus(ws!), enabled: Boolean(ws) });

  useEffect(() => { const p = take(); if (p) void ask(p.question, p.origin); }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [turns]);

  const src = useSourceLink();
  const open = (s: AssistantSource) => src.open(s.link);
  const submit = (e?: FormEvent) => { e?.preventDefault(); if (!q.trim()) return; void ask(q, origin); setQ(''); };
  const st = status.data;
  const busy = turns.some(t => t.status === 'loading');

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2 text-xs dark:border-slate-800">
        {st && (st.mode === 'llm'
          ? <Badge color="#7c3aed"><Sparkles className="mr-1 inline h-3 w-3" />AI answers · {st.model}</Badge>
          : <Badge color="#64748b"><Search className="mr-1 inline h-3 w-3" />Search-only</Badge>)}
        {st && <span className="text-slate-500">{st.retrieval === 'hybrid' ? 'keyword + semantic' : 'keyword search'} · {Math.max(0, st.queries_per_hour - st.used_this_hour)}/{st.queries_per_hour} left this hour</span>}
        {turns.length > 0 && <button onClick={clear} className="ml-auto flex items-center gap-1 text-slate-500 hover:text-rose-600"><Trash2 className="h-3 w-3" />Clear</button>}
      </div>
      <div className="scroll-thin min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        {!turns.length && (
          <div className="space-y-3 pt-4 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-accent-fg" />
            <p className="text-sm font-medium">Ask about anything in your chats, tasks, files and buildings.</p>
            <p className="text-xs text-slate-500">Answers only use what you can already open, and link back to the source.</p>
            <div className="flex flex-wrap justify-center gap-1.5 pt-2">
              {EXAMPLES.map(x => <button key={x} onClick={() => void ask(x, origin)} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:border-accent-line dark:border-slate-700 dark:text-slate-300">{x}</button>)}
            </div>
          </div>
        )}
        {turns.map(t => (
          <div key={t.id} className="space-y-2">
            <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-accent px-3 py-2 text-sm text-on-accent">{t.question}</div>
            {t.status === 'loading' ? <div className="flex items-center gap-2 text-xs text-slate-500"><Spinner className="h-4 w-4" />Searching what you have access to…</div>
              : t.status === 'error' ? <div className="rounded-md bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-200">{t.error}</div>
              : t.result && <Answer r={t.result} onOpen={open} />}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={submit} className="flex items-end gap-2 border-t border-slate-200 p-3 dark:border-slate-800">
        <textarea value={q} onChange={e => setQ(e.target.value)} rows={1} maxLength={1000} autoFocus={autoFocus} placeholder="Ask a question…"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          className="max-h-32 min-h-9 flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-accent-line dark:border-slate-700 dark:bg-slate-900" />
        <Button type="submit" size="icon" disabled={!q.trim() || busy} aria-label="Ask"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}

/** #52: the action assistant when it's switched on and the person holds ai.assistant.use; the #8 workplace search otherwise. */
export function useAssistantModes() {
  const ws = useWorkspaceId();
  const canAct = useCanAnywhere('ai.assistant.use');
  const canSearch = useCanAnywhere('assistant.use');
  const st = useQuery({ queryKey: ['ai-chat', ws, 'status'], queryFn: () => api.assistantChatStatus(ws!), enabled: Boolean(ws) && canAct, retry: false });
  const act = canAct && Boolean(st.data?.available);
  return { act, search: canSearch, loading: canAct && st.isLoading };
}

export type Mode = 'act' | 'search';
export function ModeTabs({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) {
  return (
    <div className="flex gap-1 border-b border-slate-200 px-3 pt-1 text-xs dark:border-slate-800" role="tablist">
      {([['act', 'Ask & do'], ['search', 'Search workplace']] as const).map(([id, label]) => (
        <button key={id} role="tab" aria-selected={mode === id} onClick={() => setMode(id)}
          className={clsx('-mb-px border-b-2 px-2 py-1.5 font-semibold', mode === id ? 'border-black text-slate-900 dark:border-white dark:text-white' : 'border-transparent text-slate-500 hover:text-slate-800')}>{label}</button>
      ))}
    </div>
  );
}

/** Side drawer on desktop, full screen on phones. Opened from the top bar, Ctrl+K "Ask the assistant" or /ask in a chat. */
export function AssistantPanel() {
  const close = useUi(s => s.closeAssistant);
  const nav = useNavigate();
  const narrow = useNarrow();
  const modes = useAssistantModes();
  const [mode, setMode] = useState<Mode>('act');
  const m: Mode = modes.act && (mode === 'act' || !modes.search) ? 'act' : 'search';
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && narrow) close(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [narrow, close]);
  return (
    <aside className={clsx('flex flex-col bg-white dark:bg-slate-950', narrow ? 'fixed inset-0 z-50' : 'w-[28rem] shrink-0 border-l border-slate-200 dark:border-slate-800')} aria-label="Assistant" data-testid="assistant-panel">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-800">
        <Sparkles className="h-4 w-4 text-accent-fg" /><span className="font-semibold">Assistant</span>
        <button onClick={() => { close(); nav('/assistant'); }} className="ml-auto text-xs text-slate-500 hover:text-slate-900 dark:hover:text-white">Open full page</button>
        <CloseLink label="Close assistant" onClose={close} />
      </div>
      {modes.act && modes.search && <ModeTabs mode={m} setMode={setMode} />}
      {modes.loading ? <div className="p-4"><Spinner /></div> : m === 'act' ? <ActionChat autoFocus={!narrow} /> : <AssistantConversation origin="panel" autoFocus={!narrow} />}
    </aside>
  );
}

function History({ scope }: { scope: 'mine' | 'all' }) {
  const ws = useWorkspaceId();
  const openAssistant = useUi(s => s.openAssistant);
  const q = useQuery({ queryKey: ['assistant', ws, 'queries', scope], queryFn: () => api.assistantQueries(ws!, { scope, limit: 100 }), enabled: Boolean(ws) });
  if (q.isLoading) return <Spinner />;
  const rows = q.data?.queries ?? [];
  if (!rows.length) return <p className="text-sm text-slate-500">No questions yet.</p>;
  const tone: Record<string, string> = { ok: '#059669', no_results: '#64748b', llm_error: '#c2410c', rate_limited: '#e11d48' };
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800">
      {rows.map(r => (
        <div key={r.id} className="flex items-start gap-3 py-2 text-sm">
          <div className="min-w-0 flex-1">
            <button className="text-left hover:text-accent-fg" onClick={() => openAssistant(r.question, 'page')} title="Ask again">{r.question}</button>
            <div className="text-xs text-slate-500">
              {scope === 'all' && <>{r.display_name} · </>}<RelativeTime iso={r.created_at} /> · {r.mode === 'llm' ? r.model : 'search-only'} · {r.retrieval} · {r.source_refs.length} sources{r.cited_count ? `, ${r.cited_count} cited` : ''} · {r.latency_ms} ms · via {r.origin}
            </div>
          </div>
          <Badge color={tone[r.status]}>{sentenceCase(r.status)}</Badge>
        </div>
      ))}
    </div>
  );
}

export default function AssistantPage() {
  const isAdmin = useCan('assistant.audit.view');
  const modes = useAssistantModes();
  const [tab, setTab] = useState<'do' | 'ask' | 'mine' | 'all'>('do');
  const t = tab === 'do' && !modes.act ? 'ask' : tab;
  return (
    <div className="flex h-full flex-col">
      <PageHeader icon={<Sparkles className="h-5 w-5" />} title="Assistant" />
      <Tabs className="px-3 sm:px-6" value={t} onChange={setTab} tabs={[...(modes.act ? [{ id: 'do' as const, label: 'Ask & do' }] : []), { id: 'ask', label: 'Search workplace' }, { id: 'mine', label: 'My questions' }, ...(isAdmin ? [{ id: 'all' as const, label: 'Audit log (all users)' }] : [])]} />
      {t === 'do' ? (
        <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col"><ActionChat autoFocus /></div>
      ) : t === 'ask' ? (
        <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col"><AssistantConversation origin="page" autoFocus /></div>
      ) : (
        <div className="scroll-thin flex-1 overflow-y-auto p-6"><div className="mx-auto w-full max-w-5xl"><Card title={t === 'all' ? 'Every assistant question in this workspace' : 'Your recent questions'}><History scope={t as 'mine' | 'all'} /></Card></div></div>
      )}
    </div>
  );
}
